import {
  CAMPAIGN_STEP_CONVERSION_QUERY_PARAM_KEY,
  IBrand,
  IMarketingCampaignEntry,
  IMarketingCampaignStep,
  IOrganisation,
  IPatient,
  IPatientPortalToken,
  IPatientTokenData,
  IPractice,
} from '@principle-theorem/principle-core/interfaces';
import {
  DocumentReference,
  Firestore,
  IReffable,
  WithRef,
} from '@principle-theorem/shared';
import { createCSRF } from '@principle-theorem/temporary-tokens';
import * as moment from 'moment-timezone';
import { getTokenExpiryFromAppointment } from '../appointment/patient-confirm-appointment';
import { Brand } from '../brand';
import { Organisation } from '../organisation/organisation';
import { Patient } from './patient';

export async function generatePatientMedicalHistoryToken(
  org: WithRef<IOrganisation>,
  patient: WithRef<IPatient>,
  campaignStepSource?: DocumentReference<IMarketingCampaignStep>
): Promise<string> {
  const appointment = await Patient.getNextScheduledAppointment(patient);
  const tokenExpiry = await getTokenExpiryFromAppointment(appointment);
  const tokenData: IPatientTokenData = {
    patient: patient.ref,
    campaignStepSource,
  };
  const csrf = await createCSRF(
    Organisation.temporaryTokenCol(org),
    tokenData,
    tokenExpiry,
    moment(tokenExpiry).add(1, 'week'),
    true
  );

  return csrf.tokenUid;
}

export async function generateMedicalHistoryUrl(
  patient: WithRef<IPatient>,
  appUrl: string
): Promise<string> {
  const brandRef = Firestore.getParentDocRef<IBrand>(patient.ref.path);
  const orgRef = Firestore.getParentDocRef<IOrganisation>(brandRef.path);
  const token = await generatePatientMedicalHistoryToken(
    await Firestore.getDoc(orgRef),
    patient
  );

  return [appUrl, 'history', token].join('/');
}

/**
 * Generates a patient forms link that expires in 2 weeks
 */
export async function generatePatientFormsToken(
  org: IReffable<IOrganisation>,
  patient: IReffable<IPatient>,
  paractice: IReffable<IPractice>
): Promise<string> {
  const tokenExpiry = moment().add(2, 'weeks');
  const tokenData: IPatientPortalToken = {
    patient: patient.ref,
    practice: paractice.ref,
  };
  const csrf = await createCSRF(
    Organisation.temporaryTokenCol(org),
    tokenData,
    tokenExpiry,
    moment(tokenExpiry).add(1, 'week'),
    true
  );

  return csrf.tokenUid;
}

export async function generatePatientFormsUrl(
  patient: IReffable<IPatient>,
  practice: IReffable<IPractice>,
  appUrl: string
): Promise<string> {
  const brandRef = Patient.brandRef(patient);
  const orgRef = Brand.organisationRef({ ref: brandRef });
  const org = await Firestore.getDoc(orgRef);
  const token = await generatePatientFormsToken(org, patient, practice);
  return [appUrl, 'forms', token].join('/');
}

export function generateAppointmentBookingUrl(
  practice: IReffable<IPractice>,
  appUrl: string,
  conversionUid?: string
): string {
  const url = `${appUrl}/portal/booking/${practice.ref.id}`;

  if (conversionUid) {
    return `${url}?${CAMPAIGN_STEP_CONVERSION_QUERY_PARAM_KEY}=${conversionUid}`;
  }

  return url;
}

export async function generateUnsubscribeUrl(
  patient: IReffable<IPatient>,
  practice: IReffable<IPractice>,
  appUrl: string,
  campaignEntry?: WithRef<IMarketingCampaignEntry>
): Promise<string> {
  const token = await Patient.generateUnsubscribeToken(
    patient.ref,
    practice.ref,
    campaignEntry?.ref
  );

  return `${appUrl}/unsubscribe/${token}`;
}
